import { Check } from "lucide-react";
import { ProductDetails } from "../types/global";
import {
  getMonthlyDisplayPrice,
  getAnnualDisplayPrice,
  getSavingsPercentage,
  shouldShowRecurringToggle,
  isRecurringToggleDisabled,
  getCurrencySymbol,
  formatPrice,
  isFreeProduct,
  isCustomProduct,
  isPricingAvailable,
} from "../utils/pricingViewUtils";

interface ProductCardNewProps {
  product: ProductDetails;
  selectedCurrency: string;
  billingPeriod: "monthly" | "annual";
  currentPlanProductId: number | null;
  onSelectPlan: (productId: number) => void;
  descriptionRef?: (el: HTMLDivElement | null) => void;
  featuresRef?: (el: HTMLDivElement | null) => void;
  descriptionHeight: number | null;
  featuresHeight: number | null;
  recurringOption: "recurring" | "onetime";
  onToggleRecurring: () => void;
  isPromoted?: boolean;
  badge?: string | null;
  features?: { description: string }[];
  isLoading?: boolean;
  isAnyLoading?: boolean;
}

const ProductCardNew = ({
  product,
  selectedCurrency,
  billingPeriod,
  currentPlanProductId,
  onSelectPlan,
  descriptionRef,
  featuresRef,
  descriptionHeight,
  featuresHeight,
  recurringOption,
  onToggleRecurring,
  isPromoted = false,
  badge = null,
  features = [],
  isLoading = false,
  isAnyLoading = false,
}: ProductCardNewProps) => {
  const isFree = isFreeProduct(product);
  const isCustom = isCustomProduct(product);
  const isCurrentPlan = product.id === currentPlanProductId;
  const showRecurringToggle = shouldShowRecurringToggle(product, selectedCurrency);
  const hasPricing = isPricingAvailable(product, selectedCurrency, billingPeriod, recurringOption);

  const toggleDisabled = isRecurringToggleDisabled(product, selectedCurrency);

  const monthlyPrice = getMonthlyDisplayPrice(
    product,
    selectedCurrency,
    billingPeriod,
    recurringOption
  );
  const annualPrice = getAnnualDisplayPrice(product, selectedCurrency, recurringOption);
  const savings = getSavingsPercentage(product, selectedCurrency, recurringOption);

  const getButtonLabel = () => {
    if (isCurrentPlan) return "Current Plan";
    return "Select Plan";
  };

  return (
    <div
      className={`relative bg-white rounded-2xl p-8 transition-all w-[340px] flex-shrink-0 shadow-lg hover:shadow-xl border flex flex-col h-full ${
        isPromoted
          ? "ring-2 ring-purple-500 border-purple-200"
          : "border-slate-200"
      }`}
    >
      {/* Gradient overlay - top 60% */}
      <div
        className={`absolute top-0 left-0 right-0 h-[60%] rounded-t-2xl -z-0 ${
          isPromoted
            ? "bg-gradient-to-b from-purple-100 to-transparent"
            : "bg-gradient-to-b from-slate-100 to-transparent"
        }`}
      />

      {/* Badge */}
      {isPromoted && badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20">
          <span className="bg-purple-500 text-white px-4 py-1 rounded-full whitespace-nowrap flex items-center gap-1">
            <svg
              className="w-4 h-4"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            {badge}
          </span>
        </div>
      )}

      <div className="relative z-10 flex flex-col flex-1">
        {/* Product Name */}
        <h3 className="text-slate-900 mb-3 text-3xl">
          {product.name}
        </h3>

        {/* Description */}
        <div
          ref={descriptionRef}
          className="text-slate-500 mb-6"
          style={{
            minHeight: descriptionHeight ? `${descriptionHeight}px` : 'auto'
          }}
        >
          {product.description}
        </div>

        {/* Price */}
        <div>
          {isFree ? (
            <div>
              <span className="text-slate-900 text-5xl">Free</span>
              {/* Invisible placeholder for consistent spacing - only for annual */}
              {billingPeriod === "annual" && (
                <div className="mt-1 invisible">
                  <span className="text-slate-500 text-lg">placeholder per annum</span>
                </div>
              )}
            </div>
          ) : isCustom ? (
            <div>
              <span className="text-slate-900 text-5xl">Custom</span>
              {/* Invisible placeholder for consistent spacing - only for annual */}
              {billingPeriod === "annual" && (
                <div className="mt-1 invisible">
                  <span className="text-slate-500 text-lg">placeholder per annum</span>
                </div>
              )}
            </div>
          ) : hasPricing && monthlyPrice !== null ? (
            <div>
              <div>
                <span className="text-slate-900 text-5xl">
                  {getCurrencySymbol(selectedCurrency)}{formatPrice(monthlyPrice, selectedCurrency)}
                </span>
                <span className="text-slate-500 text-xl ml-1">/ month</span>
              </div>
              {/* Annual price - only render for annual billing */}
              {billingPeriod === "annual" && annualPrice !== null && (
                <div className="mt-1">
                  <span className="text-slate-500 text-lg">
                    {getCurrencySymbol(selectedCurrency)}{formatPrice(annualPrice, selectedCurrency)} per annum
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div>
              <span className="text-slate-900 text-5xl">N/A</span>
              <div className="text-slate-500 text-sm mt-2">
                Not available for {selectedCurrency}
              </div>
            </div>
          )}
        </div>

        {/* Savings badge for annual */}
        <div className="mb-4 h-[20px]">
          {billingPeriod === "annual" && recurringOption === "recurring" && savings && savings > 0 && (
            <div>
              <span className="text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full text-sm whitespace-nowrap">
                Save {savings}%!
              </span>
            </div>
          )}
        </div>

        {/* Features List */}
        <div
          ref={featuresRef}
          className="mb-8"
          style={{
            minHeight: featuresHeight ? `${featuresHeight}px` : 'auto'
          }}
        >
          {features.length > 0 && (
            <div className="border-t border-slate-200 pt-6">
              <h4 className="text-slate-900 mb-4">
                Features
              </h4>
              <ul className="space-y-3 p-0 m-0 list-none">
                {features.map((feature, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3 text-slate-600"
                  >
                    <div className="w-5 h-5 rounded-full border-2 border-purple-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-purple-600" />
                    </div>
                    <span className="flex-1 text-left">{feature.description}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Badge at bottom */}
        {!isPromoted && badge && (
          <div className="text-center mb-4">
            <span className="text-purple-600 bg-purple-100 px-3 py-1.5 rounded-full inline-block">
              {badge}
            </span>
          </div>
        )}
        {(!badge || isPromoted) && <div className="mb-4 h-[34px]"></div>}

        {/* CTA Button */}
        <button
          disabled={isCurrentPlan || isCustom || (!hasPricing && !isFree) || isAnyLoading}
          onClick={() => !isCustom && (hasPricing || isFree) && !isAnyLoading && onSelectPlan(product.id)}
          className={`w-full py-3 px-6 rounded-lg transition-colors duration-200 mb-4 flex items-center justify-center gap-2 ${
            isCurrentPlan
              ? "bg-slate-200 text-slate-500 cursor-not-allowed"
              : !hasPricing && !isFree
              ? "bg-slate-300 text-slate-500 cursor-not-allowed"
              : isCustom
                ? "bg-slate-900 text-white hover:bg-slate-800"
                : isAnyLoading
                  ? "bg-purple-400 text-white cursor-not-allowed"
                  : isFree
                    ? "border-2 border-purple-600 text-purple-600 bg-white hover:bg-purple-50"
                    : "bg-purple-600 text-white hover:bg-purple-700"
          }`}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </>
          ) : (
            getButtonLabel()
          )}
        </button>

        {/* Payment Type Switch - always reserve space */}
        <div
          className="flex items-center justify-center gap-3 h-[32px]"
          style={{ opacity: showRecurringToggle ? 1.0 : 0 }}
        >
          <span
            className={`text-base ${
              toggleDisabled
                ? "text-slate-400"
                : recurringOption === "onetime"
                ? "text-slate-900"
                : "text-slate-500"
            }`}
          >
            One-time
          </span>
          <button
            onClick={onToggleRecurring}
            disabled={toggleDisabled}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              toggleDisabled
                ? "bg-slate-200 cursor-not-allowed opacity-50"
                : recurringOption === "recurring"
                ? "bg-purple-600"
                : "bg-slate-300"
            }`}
          >
            <div
              className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                recurringOption === "recurring"
                  ? "translate-x-7"
                  : "translate-x-1"
              }`}
            />
          </button>
          <span
            className={`text-base ${
              toggleDisabled
                ? "text-slate-400"
                : recurringOption === "recurring"
                ? "text-slate-900"
                : "text-slate-500"
            }`}
          >
            Recurring
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProductCardNew;
